import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  Typography,
  Box,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  ArrowForward,
  Google,
  Facebook
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import PhoneNumberDialog from '../components/PhoneNumberDialog';
// No longer using @react-oauth/google
import '../styles/auth.css';
import '../styles/auth-fix.css';
import '../styles/logo.css';
import logo from '../assets/logowht.png';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, googleLogin, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPhoneDialog, setShowPhoneDialog] = useState(false);
  const [phoneDialogError, setPhoneDialogError] = useState('');
  const [phoneDialogLoading, setPhoneDialogLoading] = useState(false);
  const [pendingGoogleData, setPendingGoogleData] = useState(null);

  // Get the redirect path from location state or default to dashboard
  const from = location.state?.from?.pathname || '/dashboard';

  // Check if already authenticated and redirect if needed
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Don't proceed if already loading
    if (isLoading) return;

    // Clear previous messages
    setError('');
    setSuccess('');

    // Validate form
    if (!formData.email || !formData.password) {
      setError('Please fill in all required fields');
      return;
    }

    // Show loading state
    setIsLoading(true);

    try {
      // Use the login function from AuthContext
      const response = await login(formData);

      // Show success message
      setSuccess(`Welcome back, ${response.user.username}! Redirecting to dashboard...`);

      // Short delay before navigation to show the success message
      setTimeout(() => {
        // Navigate programmatically instead of relying on the useEffect
        navigate(from);
      }, 1500);
    } catch (err) {
      console.error('Login error:', err);

      // Clear password field on failed login
      setFormData(prev => ({
        ...prev,
        password: ''
      }));

      // Handle different error types
      if (err.status === 401) {
        setError('Invalid email or password. Please try again.');
      } else if (err.status === 403) {
        setError('Your account is inactive or suspended. Please contact support.');
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Login failed. Please check your credentials and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');

      const response = await googleLogin();

      // Show success message based on whether it's a new or existing user
      if (response.needsTenantSetup) {
        setSuccess('Google sign-in successful! Please complete your tenant setup.');

        // Short delay before navigation to show the success message
        setTimeout(() => {
          navigate('/complete-registration');
        }, 1500);
      } else {
        setSuccess(`Welcome back, ${response.user.username || 'User'}! Redirecting to dashboard...`);

        // Short delay before navigation to show the success message
        setTimeout(() => {
          navigate(from);
        }, 1500);
      }
    } catch (err) {
      console.error('Firebase Google login error:', err);

      // Check if phone number is needed (check both locations)
      if (err.needsPhoneNumber || err.response?.data?.needsPhoneNumber) {
        // Store the Google data and show phone dialog
        setPendingGoogleData({
          error: err,
          retryLogin: true
        });
        setShowPhoneDialog(true);
        setIsLoading(false);
        return;
      }

      // Handle different error types
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Google sign-in was cancelled. Please try again.');
      } else if (err.code === 'auth/popup-blocked') {
        setError('Google sign-in popup was blocked. Please allow popups for this site.');
      } else if (err.status === 401) {
        setError('Authentication failed. Please try again.');
      } else if (err.status === 403) {
        setError('Your account is inactive or suspended. Please contact support.');
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Google login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneNumberSubmit = async (phoneNumber) => {
    try {
      setPhoneDialogLoading(true);
      setPhoneDialogError('');

      // Retry Google login with phone number
      const response = await googleLogin(phoneNumber);

      // Close dialog
      setShowPhoneDialog(false);

      // Show success and navigate
      if (response.needsTenantSetup) {
        setSuccess('Registration successful! Please complete your tenant setup.');
        setTimeout(() => {
          navigate('/complete-registration');
        }, 1500);
      } else {
        setSuccess(`Welcome, ${response.user.username || 'User'}! Redirecting to dashboard...`);
        setTimeout(() => {
          navigate(from);
        }, 1500);
      }
    } catch (err) {
      console.error('Phone number submission error:', err);
      setPhoneDialogError(err.message || 'Failed to complete registration. Please try again.');
    } finally {
      setPhoneDialogLoading(false);
    }
  };

  const handlePhoneDialogClose = () => {
    setShowPhoneDialog(false);
    setPendingGoogleData(null);
    setPhoneDialogError('');
  };

  return (
    <div className="auth-container">
      {/* Left Panel */}
      <div className="auth-left-panel">
        <div className="auth-left-content">
          <div className="auth-logo">
            <img
              src={logo}
              alt="Eazy Fatoora Logo"
              style={{
                height: '120px',
                width: 'auto',
                objectFit: 'contain',
                marginLeft: '-45px'
              }}
            />
          </div>
          <h1 className="auth-headline">Streamline your business operations</h1>
          <p className="auth-subheadline">One integrated platform to manage your entire business workflow.</p>

          <div className="auth-features">
            <div className="auth-feature">
              <h3 className="auth-feature-title">Inventory Management</h3>
              <p className="auth-feature-description">Track and manage your inventory with real-time updates</p>
            </div>

            <div className="auth-feature">
              <h3 className="auth-feature-title">Financial Reports</h3>
              <p className="auth-feature-description">Generate insightful reports for informed decision-making</p>
            </div>

            <div className="auth-feature">
              <h3 className="auth-feature-title">Customer Management</h3>
              <p className="auth-feature-description">Manage relationships and improve customer satisfaction</p>
            </div>

            <div className="auth-feature">
              <h3 className="auth-feature-title">Resource Planning</h3>
              <p className="auth-feature-description">Optimize resource allocation for maximum efficiency</p>
            </div>
          </div>
        </div>

        <div className="auth-footer" style={{ width: "100%", display: "flex", justifyContent: "center", marginLeft: "-40px" }}>
          {new Date().getFullYear()} All rights reserved.
        </div>

        <div className="animated-background">
          <div className="background-shape shape1"></div>
          <div className="background-shape shape2"></div>
          <div className="background-shape shape3"></div>
          <svg width="0" height="0">
            <filter id="noise">
              <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" />
              <feBlend mode="screen" />
            </filter>
          </svg>
        </div>
      </div>

      {/* Right Panel */}
      <div className="auth-right-panel">
        <div className="auth-form-container">
          <h2 className="auth-form-title">Welcome back</h2>
          <p className="auth-form-subtitle">Sign in to access your account</p>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="auth-card">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <div className="input-container">
                <Email className="input-icon" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-container">
                <Lock className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={handleClickShowPassword}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </button>
              </div>
            </div>

            <div className="form-actions">
              <div className="remember-me">
                <input
                  type="checkbox"
                  id="remember-me"
                  checked={rememberMe}
                  onChange={() => setRememberMe(!rememberMe)}
                />
                <label htmlFor="remember-me">Remember me</label>
              </div>
              <Link to="/forgot-password" className="forgot-password">Forgot password?</Link>
            </div>

            <button
              type="submit"
              className="auth-button"
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <>Sign in</>
              )}
            </button>

            {/* <div className="divider">
              <span>Or continue with</span>
            </div> */}

            {/* <div className="social-buttons">
              <button
                type="button"
                className="social-button google"
                onClick={handleGoogleLogin}
                disabled={isLoading}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Google
              </button>
              <button
                type="button"
                className="social-button microsoft"
                disabled={isLoading}
              >
                <svg width="20" height="20" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#f25022" d="M1 1h10v10H1z" />
                  <path fill="#00a4ef" d="M1 12h10v10H1z" />
                  <path fill="#7fba00" d="M12 1h10v10H12z" />
                  <path fill="#ffb900" d="M12 12h10v10H12z" />
                </svg>
                Microsoft
              </button>
            </div> */}
            {/*             
            <div className="auth-footer-links">
              Don't have an account? <Link to="/register">Sign up</Link>
            </div> */}
          </form>
        </div>
      </div>

      {/* Phone Number Dialog */}
      <PhoneNumberDialog
        open={showPhoneDialog}
        onClose={handlePhoneDialogClose}
        onSubmit={handlePhoneNumberSubmit}
        loading={phoneDialogLoading}
        error={phoneDialogError}
      />
    </div>
  );
}

export default Login;